import React from 'react';
import { Info, Package, ListFilter, FileText, Lock, Unlock, X } from 'lucide-react';
import { OrderSheet, Store } from '../../../types';

interface OrderSummaryCardProps {
  summary: {
    totalOrders: number;
    totalAmount: number;
    totalItems: number;
    totalSkus: number;
  };
  selectedOrderSheetIds: string[];
  selectedSheet?: OrderSheet;
  stores: Store[];
  isLatestSheet: boolean;
  onRefresh?: () => void;
  onSettle?: (storeId?: string) => void;
  onOrderSheetChange: (ids: string[]) => void;
  setIsManagingProducts: (value: boolean) => void;
  setIsEditing: (value: boolean) => void;
  setEditSheetData: (data: Partial<OrderSheet>) => void;
}

export const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  summary,
  selectedOrderSheetIds,
  selectedSheet,
  stores,
  isLatestSheet,
  onRefresh,
  onSettle,
  onOrderSheetChange,
  setIsManagingProducts,
  setIsEditing,
  setEditSheetData,
}) => {
  return (
    <div className="bg-gray-900 text-white p-6 md:p-8 rounded-[32px] shadow-xl space-y-6 print:hidden">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black opacity-60 uppercase tracking-wider">현재 주차 요약</span>
            {selectedOrderSheetIds.includes('all') ? (
              <span className="text-[10px] font-black px-1.5 py-0.5 bg-white/10 rounded-md text-emerald-400">전체 내역</span>
            ) : selectedSheet ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black px-1.5 py-0.5 bg-white/10 rounded-md text-emerald-400">
                  {stores.find(s => s.id === selectedSheet.storeId)?.name} | {selectedSheet.label}
                </span>
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-md ${selectedSheet.isClosed ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                  {selectedSheet.isClosed ? '마감됨' : '진행중'}
                </span>
              </div>
            ) : null}
          </div>
          <h3 className="text-sm md:text-base font-black">실시간 주문 현황</h3>
        </div>
        <Info size={16} className="opacity-40" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
        <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
          <p className="text-[10px] font-bold opacity-50 mb-1">총 주문고객</p>
          <p className="text-xl md:text-2xl font-black truncate">{summary.totalOrders}명</p>
        </div>
        <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
          <p className="text-[10px] font-bold opacity-50 mb-1">총 품목수</p>
          <p className="text-xl md:text-2xl font-black truncate">{summary.totalSkus}종</p>
        </div>
        <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
          <p className="text-[10px] font-bold opacity-50 mb-1">총 상품수량</p>
          <p className="text-xl md:text-2xl font-black truncate">{summary.totalItems}개</p>
        </div>
        <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
          <p className="text-[10px] font-bold opacity-50 mb-1">총 주문금액</p>
          <p className="text-xl md:text-2xl font-black truncate">{summary.totalAmount.toLocaleString()}원</p>
        </div>
      </div>
      {selectedSheet && (
        <div className="pt-2 border-t border-white/10 space-y-2">
          <div className="flex justify-between items-center text-[9px] font-bold opacity-70">
            <div className="flex gap-3">
              <span>배송일: {new Date(selectedSheet.deliveryDate).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Package size={10} />
              <span>등록 상품: <span className={selectedSheet.products?.length ? 'text-emerald-400' : 'text-red-400'}>{selectedSheet.products?.length || 0}종</span></span>
            </div>
          </div>

          {(!selectedSheet.products || selectedSheet.products.length === 0) && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 flex items-center gap-2">
              <Info size={12} className="text-red-400" />
              <p className="text-[9px] font-bold text-red-200">현재 주문서에 등록된 상품이 없습니다. 아래 버튼을 눌러 상품을 추가해주세요.</p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => {
                setIsManagingProducts(true);
              }}
              className="flex-1 py-2 bg-white/10 rounded-xl text-[10px] font-black hover:bg-white/20 transition-all flex items-center justify-center gap-1.5"
            >
              <ListFilter size={12} /> 주문서 상품 구성 관리
            </button>
            <button
              onClick={() => {
                setEditSheetData({
                  id: selectedSheet.id,
                  label: selectedSheet.label,
                  deliveryDate: selectedSheet.deliveryDate.split('T')[0],
                  orderingEndDate: selectedSheet.orderingEndDate,
                });
                setIsEditing(true);
              }}
              className="flex-1 py-2 bg-white/10 rounded-xl text-[10px] font-black hover:bg-white/20 transition-all flex items-center justify-center gap-1.5"
            >
              <FileText size={12} /> 주문서 수정
            </button>
            {!selectedSheet.isClosed && (
              <button
                onClick={() => {
                  if (onSettle) onSettle(selectedSheet.storeId);
                }}
                className="flex-1 py-2 bg-indigo-600 rounded-xl text-[10px] font-black hover:bg-indigo-700 transition-all flex items-center justify-center gap-1.5 text-white"
              >
                <Lock size={12} /> 영업 마감
              </button>
            )}
            {selectedSheet.isClosed && isLatestSheet && (
              <button
                onClick={async () => {
                  if (confirm('마감을 해제하시겠습니까? 다시 주문을 받을 수 있는 상태가 됩니다.')) {
                    const { updateOrderSheet } = await import('../../../api/order-sheets');
                    try {
                      const deliveryDate = selectedSheet.deliveryDate.split('T')[0];
                      await updateOrderSheet(selectedSheet.id, {
                        isClosed: false,
                        orderingEndDate: `${deliveryDate}T23:59:59`
                      });
                      alert('마감이 해제되었습니다.');
                      if (onRefresh) onRefresh();
                    } catch (e) {
                      console.error(e);
                      alert('해제 실패');
                    }
                  }
                }}
                className="flex-1 py-2 bg-orange-500 rounded-xl text-[10px] font-black hover:bg-orange-600 transition-all flex items-center justify-center gap-1.5 text-white"
              >
                <Unlock size={12} /> 마감 해제
              </button>
            )}
            <button
              onClick={async () => {
                if (confirm('정말 이 주문서를 삭제하시겠습니까? 관련 주문 데이터도 모두 삭제됩니다.')) {
                  const { deleteOrderSheet } = await import('../../../api/order-sheets');
                  try {
                    await deleteOrderSheet(selectedSheet.id);
                    alert('삭제되었습니다.');
                    onOrderSheetChange(['all']);
                    if (onRefresh) onRefresh();
                  } catch (e) {
                    console.error(e);
                    alert('삭제 실패');
                  }
                }
              }}
              className="flex-1 py-2 bg-red-600/20 text-red-400 rounded-xl text-[10px] font-black hover:bg-red-600/30 transition-all flex items-center justify-center gap-1.5"
            >
              <X size={12} /> 삭제
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
